import { useState, useCallback, useEffect } from 'react';
import PremiumAIService from '../services/premiumAIService';
import api from '../config/api';
import { enqueueSnackbar } from 'notistack';
import { appendAiItineraryToTrip } from '../utils/appendAiItinerary';
import { AI_SEARCH_STEPS, createEmptyAiSearchProgress } from '../utils/aiSearchProgress';

const PREMIUM_AI_STORAGE_KEY = 'premium_ai_recommendations';
const PREMIUM_AI_STATE_KEY = 'premium_ai_state';
const AI_CONCIERGE_PLANNING_TRIP_KEY = 'ai_concierge_planning_trip_id';

export { AI_SEARCH_STEPS, createEmptyAiSearchProgress } from '../utils/aiSearchProgress';

// Global flag to prevent multiple hook instances from restoring simultaneously
let isRestoring = false;

export const usePremiumAI = (preferences = {}, { planningTripId = null, tripId = null, usePartnerAuth = false } = {}) => {
  const [recommendations, setRecommendations] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [searchProgress, setSearchProgress] = useState(createEmptyAiSearchProgress);
  const [error, setError] = useState(null);
  const [isConverting, setIsConverting] = useState(false);
  const [hasRestored, setHasRestored] = useState(false);
  
  // Simple state management
  const [buttonState, setButtonState] = useState({
    disabled: false,
    text: 'Generate My Trip',
    showLoading: false
  });

  // Load state from localStorage on mount
  useEffect(() => {
    if (hasRestored || isRestoring) return; // Prevent multiple restorations
    
    isRestoring = true;
    console.log('🔍 Loading premium AI state from localStorage...');
    
    try {
      const storedRecommendations = localStorage.getItem(PREMIUM_AI_STORAGE_KEY);
      const storedState = localStorage.getItem(PREMIUM_AI_STATE_KEY);
      
      if (storedRecommendations) {
        const parsedRecommendations = JSON.parse(storedRecommendations);
        console.log('🔄 Restoring premium AI recommendations from localStorage');
        setRecommendations(parsedRecommendations);
        // If we have results, update button state
        setButtonState({
          disabled: false,
          text: 'Generate My Trip',
          showLoading: false
        });
      }
      
      if (storedState) {
        const parsedState = JSON.parse(storedState);
        console.log('🔄 Restoring premium AI state from localStorage:', parsedState);
        setSelectedOptions(parsedState.selectedOptions || {});
        
        // Check if generation was in progress
        const wasGenerating = parsedState.isGenerating || false;
        const generationStartTime = parsedState.generationStartTime;
        const now = Date.now();
        
        if (wasGenerating && generationStartTime) {
          const generationAge = now - generationStartTime;
          const maxGenerationTime = 4 * 60 * 1000; // 4 minutes
          
          if (generationAge > maxGenerationTime) {
            console.log('⏰ Generation timeout detected, stopping generation');
            setIsGenerating(false);
            setError('Generation timed out. Please try again.');
            setButtonState({
              disabled: false,
              text: 'Generate My Trip',
              showLoading: false
            });
          } else {
            console.log('🔄 Restoring generation state, age:', generationAge, 'ms');
            setIsGenerating(true);
            setButtonState({
              disabled: true,
              text: 'Generating AI recommendations...',
              showLoading: true
            });
          }
        } else {
          setIsGenerating(wasGenerating);
        }
        
        setError(parsedState.error || null);
        setIsConverting(parsedState.isConverting || false);
      }
      
      setHasRestored(true);
    } catch (error) {
      console.error('Error loading premium AI state from localStorage:', error);
      setHasRestored(true);
    } finally {
      isRestoring = false;
    }
  }, [hasRestored]);

  // Save recommendations to localStorage and update button state
  useEffect(() => {
    if (recommendations) {
      try {
        localStorage.setItem(PREMIUM_AI_STORAGE_KEY, JSON.stringify(recommendations));
        console.log('💾 Saved premium AI recommendations to localStorage');
        
        // Update button state when results are available
        setButtonState({
          disabled: false,
          text: 'Generate My Trip',
          showLoading: false
        });
      } catch (error) {
        console.error('Error saving recommendations to localStorage:', error);
      }
    }
  }, [recommendations]);

  // Save state to localStorage
  useEffect(() => {
    try {
      const state = {
        selectedOptions,
        isGenerating,
        error,
        isConverting,
        generationStartTime: isGenerating ? Date.now() : null
      };
      localStorage.setItem(PREMIUM_AI_STATE_KEY, JSON.stringify(state));
      // Only log when state actually changes significantly
      if (isGenerating || error) {
        console.log('💾 Saved premium AI state to localStorage');
      }
    } catch (error) {
      console.error('Error saving state to localStorage:', error);
    }
  }, [selectedOptions, isGenerating, error, isConverting]);

  // Listen for results from background processing
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === PREMIUM_AI_STORAGE_KEY && e.newValue) {
        try {
          const newRecommendations = JSON.parse(e.newValue);
          console.log('🎉 Results received from background processing:', newRecommendations);
          setRecommendations(newRecommendations);
          
          // Update button state when results arrive
          setButtonState({
            disabled: false,
            text: 'Generate My Trip',
            showLoading: false
          });
          
          setIsGenerating(false);
          
          // Dispatch custom event for same-tab notification
          const customEvent = new CustomEvent('ai-results-ready', {
            detail: newRecommendations
          });
          window.dispatchEvent(customEvent);
          console.log('📡 Dispatched ai-results-ready event from storage');
          
          // Success snackbar is now handled by GlobalAIResultsListener
        } catch (error) {
          console.error('Error parsing results from storage:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Generate structured recommendations
  const generateRecommendations = useCallback(async () => {
    console.log('🚀 Starting recommendation generation...');
    
    // Update button state to disabled/loading
    setButtonState({
      disabled: true,
      text: 'Generating AI recommendations...',
      showLoading: true
    });
    
    setIsGenerating(true);
    setSearchProgress(createEmptyAiSearchProgress());
    setError(null);

    // Set up timeout after 4 minutes
    const timeoutId = setTimeout(() => {
      console.log('⏰ Generation timeout after 4 minutes');
      setIsGenerating(false);
      setSearchProgress(createEmptyAiSearchProgress());
      setError('Generation timed out. Please try again.');
      setButtonState({
        disabled: false,
        text: 'Generate My Trip',
        showLoading: false
      });
      
      // Show timeout snackbar
      enqueueSnackbar('⏰ AI generation timed out. Please try again.', {
        variant: 'warning',
        autoHideDuration: 6000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' }
      });
    }, 4 * 60 * 1000); // 4 minutes

    try {
      if (planningTripId) {
        await api.post('/payments/ai-concierge/record-generation', { tripId: planningTripId });
      }

      const aiService = new PremiumAIService({
        usePartnerAuth,
        tripId: tripId || planningTripId || null,
      });
      const structuredRecommendations = await aiService.generateStructuredRecommendations(preferences, {
        onProgress: ({ step, status, count }) => {
          if (!AI_SEARCH_STEPS.includes(step) && step !== 'finalize') return;
          setSearchProgress((current) => {
            if (step === 'finalize') return current;
            return {
              ...current,
              [step]: status,
              counts: count != null
                ? { ...current.counts, [step]: count }
                : current.counts
            };
          });
        }
      });
      console.log('✅ Generated recommendations:', structuredRecommendations);
      setRecommendations(structuredRecommendations);
      
      // Save results to localStorage (this will trigger the storage event)
      try {
        localStorage.setItem(PREMIUM_AI_STORAGE_KEY, JSON.stringify(structuredRecommendations));
        console.log('💾 Saved results to localStorage');
      } catch (storageError) {
        console.warn('⚠️ Could not cache AI results in localStorage:', storageError.message);
      }
      
      // Dispatch custom event for same-tab notification
      const customEvent = new CustomEvent('ai-results-ready', {
        detail: structuredRecommendations
      });
      window.dispatchEvent(customEvent);
      console.log('📡 Dispatched ai-results-ready event');
      
      // Update button state when results arrive
      setButtonState({
        disabled: false,
        text: 'Generate My Trip',
        showLoading: false
      });

      return {
        recommendations: structuredRecommendations,
        aiUsage: aiService.getUsageTotals()
      };
    } catch (err) {
      console.error('❌ Premium AI Error:', err);
      const limitCode = err.response?.data?.code;
      if (limitCode === 'daily_cap' || limitCode === 'lifetime_cap' || limitCode === 'rate_limited') {
        setError(err.response?.data?.error || 'Generation limit reached. Please try again later.');
      } else if (limitCode === 'ai_concierge_paywall') {
        setError('AI Concierge payment is required for this trip.');
      } else {
        const detail = err.response?.data?.error || err.message;
        const isOpenAi = /openai/i.test(String(detail || ''));
        setError(
          isOpenAi
            ? `Unable to generate recommendations (${detail}). Please try again.`
            : (detail && detail !== 'Unable to generate recommendations'
              ? `Unable to generate recommendations: ${detail}`
              : 'Unable to generate recommendations. Please try again.')
        );
      }
      setButtonState({
        disabled: false,
        text: 'Generate My Trip',
        showLoading: false
      });
      
      // Show error snackbar
      enqueueSnackbar('❌ Failed to generate recommendations. Please try again.', {
        variant: 'error',
        autoHideDuration: 6000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' }
      });
    } finally {
      clearTimeout(timeoutId);
      setIsGenerating(false);
      setSearchProgress(createEmptyAiSearchProgress());
    }
  }, [preferences, planningTripId, tripId, usePartnerAuth]);

  // Cancel generation
  const cancelGeneration = useCallback(() => {
    console.log('🛑 Cancelling recommendation generation...');
    setIsGenerating(false);
    setSearchProgress(createEmptyAiSearchProgress());
    setError('Generation cancelled by user');
    setButtonState({
      disabled: false,
      text: 'Generate My Trip',
      showLoading: false
    });
  }, []);

  // Clear all state
  const clearAllState = useCallback(() => {
    console.log('🗑️ Clearing all premium AI state...');
    setRecommendations(null);
    setSelectedOptions({});
    setIsGenerating(false);
    setError(null);
    setIsConverting(false);
    setButtonState({
      disabled: false,
      text: 'Generate My Trip',
      showLoading: false
    });
    
    // Clear localStorage
    localStorage.removeItem(PREMIUM_AI_STORAGE_KEY);
    localStorage.removeItem(PREMIUM_AI_STATE_KEY);
  }, []);

  // Select/deselect an option for a category and day (multi-selection)
  const selectOption = useCallback((category, day, optionIndex) => {
    setSelectedOptions(prev => {
      const currentSelections = prev[category]?.[day] || [];
      const isSelected = currentSelections.includes(optionIndex);
      
      if (isSelected) {
        // Remove from selection
        const newSelections = currentSelections.filter(index => index !== optionIndex);
        return {
          ...prev,
          [category]: {
            ...prev[category],
            [day]: newSelections.length > 0 ? newSelections : undefined
          }
        };
      } else {
        // Add to selection
        const newSelections = [...currentSelections, optionIndex];
        return {
          ...prev,
          [category]: {
            ...prev[category],
            [day]: newSelections
          }
        };
      }
    });
  }, []);

  // Get selected option for a category and day
  const getSelectedOption = useCallback((category, day) => {
    return selectedOptions[category]?.[day];
  }, [selectedOptions]);

  // Get the final selected itinerary
  const getSelectedItinerary = useCallback(() => {
    if (!recommendations?.scheduledItinerary) return null;

    return recommendations.scheduledItinerary.map((day, dayIndex) => {
      const selectedLocationIndices = getSelectedOption('locations', dayIndex) || [];
      const selectedRestaurantIndices = getSelectedOption('restaurants', dayIndex) || [];
      const selectedActivityIndices = getSelectedOption('activities', dayIndex) || [];
      const selectedHotelIndices = getSelectedOption('hotels', dayIndex) || [];

      return {
        day: day.day,
        date: day.date,
        city: day.city,
        locations: selectedLocationIndices.length > 0 
          ? selectedLocationIndices.map(index => day.locations[index]).filter(Boolean)
          : [],
        restaurants: selectedRestaurantIndices.length > 0 
          ? selectedRestaurantIndices.map(index => day.restaurants[index]).filter(Boolean)
          : [],
        activities: selectedActivityIndices.length > 0 
          ? selectedActivityIndices.map(index => day.activities[index]).filter(Boolean)
          : [],
        hotels: selectedHotelIndices.length > 0 
          ? selectedHotelIndices.map(index => day.hotels[index]).filter(Boolean)
          : []
      };
    });
  }, [recommendations, getSelectedOption]);

  // Convert selected itinerary to trip structure
  const convertToTrip = useCallback(async (selectedItinerary) => {
    setIsConverting(true);
    setError(null);

    try {
      // Validate that user has made selections
      const hasSelections = selectedItinerary.some(day => 
        day.locations.length > 0 || 
        day.restaurants.length > 0 || 
        day.activities.length > 0 || 
        day.hotels.length > 0
      );
      
      if (!hasSelections) {
        throw new Error('Please select at least one option from each category before creating your trip.');
      }

      // Create trip data structure compatible with the application
      const destination = recommendations.destinations.join(', ');
      
      // Determine trip type based on selected preferences
      const selectedTripTypes = preferences.tripTypes || [];
      let tripType = 'vacation'; // Default fallback
      
      if (selectedTripTypes.includes('golf')) {
        tripType = 'golf';
      } else if (selectedTripTypes.includes('beach')) {
        tripType = 'beach';
      } else if (selectedTripTypes.includes('city')) {
        tripType = 'city';
      } else if (selectedTripTypes.includes('historical')) {
        tripType = 'historical';
      } else if (selectedTripTypes.includes('outdoors')) {
        tripType = 'outdoors';
      } else if (selectedTripTypes.includes('luxury')) {
        tripType = 'luxury';
      } else if (selectedTripTypes.includes('local')) {
        tripType = 'local';
      } else if (selectedTripTypes.includes('international')) {
        tripType = 'international';
      } else if (selectedTripTypes.length > 0) {
        // Use the first selected type if multiple are selected
        tripType = selectedTripTypes[0];
      }
      
      const tripData = {
        title: `${destination} - AI Generated`,
        description: `AI-generated trip with ${recommendations.totalDays} days of personalized recommendations`,
        location: recommendations.destinations.join(', '),
        start_date: preferences.startDate,
        end_date: preferences.endDate,
        trip_type: tripType,
        // Store the detailed itinerary as metadata
        ai_itinerary: selectedItinerary.map(day => ({
          day: day.day,
          date: day.date,
          city: day.city,
          activities: [
            ...day.locations.map(loc => ({
              name: loc.name,
              type: 'location',
              description: loc.description,
              time: loc.bestTime,
              cost: loc.estimatedCost,
              category: loc.category
            })),
            ...day.restaurants.map(rest => ({
              name: rest.name,
              type: 'restaurant',
              description: rest.description,
              time: rest.bestFor,
              cost: rest.priceRange,
              cuisine: rest.cuisine
            })),
            ...day.activities.map(act => ({
              name: act.name,
              type: 'activity',
              description: act.description,
              time: act.duration,
              cost: act.priceRange,
              category: act.type
            }))
          ],
          accommodation: day.hotel ? {
            name: day.hotel.name,
            description: day.hotel.description,
            cost: day.hotel.priceRange,
            rating: day.hotel.rating
          } : null
        })),
        ai_recommendations: {
          locations: recommendations.locations,
          hotels: recommendations.hotels,
          restaurants: recommendations.restaurants,
          activities: recommendations.activities
        }
      };

      console.log('Creating trip with data:', tripData);

      let tripId = planningTripId;
      let tripResponse;

      if (planningTripId) {
        tripResponse = await api.post('/payments/ai-concierge/finalize-planning-trip', {
          tripId: planningTripId,
          title: tripData.title,
          description: tripData.description,
          location: tripData.location,
          startDate: tripData.start_date,
          endDate: tripData.end_date,
          tripType: tripData.trip_type
        });
        tripResponse = { data: tripResponse.data.trip };
        localStorage.removeItem(AI_CONCIERGE_PLANNING_TRIP_KEY);
      } else {
        tripResponse = await api.post('/trips', tripData);
      }
      
      if (tripResponse.data) {
        console.log('Trip created successfully:', tripResponse.data);
        tripId = tripResponse.data.id;
        
        // Debug trip ID
        console.log('Trip ID from response:', tripId);
        console.log('Trip ID type:', typeof tripId);
        console.log('Trip ID is NaN:', isNaN(tripId));
        
        if (!tripId || isNaN(tripId)) {
          throw new Error('Invalid trip ID received from server');
        }
        
        await appendAiItineraryToTrip(
          api,
          tripId,
          selectedItinerary,
          preferences.startDate,
          preferences.endDate,
          { clientVisible: true }
        );
        return tripResponse.data;
      } else {
        throw new Error('No trip data returned from API');
      }

    } catch (err) {
      console.error('Trip Conversion Error:', err);
      setError('Unable to convert to trip. Please try again.');
      throw err;
    } finally {
      setIsConverting(false);
    }
  }, [recommendations, preferences, planningTripId]);

  // Clear all selections
  const clearSelections = useCallback(() => {
    setSelectedOptions({});
  }, []);

  // Reset recommendations
  const resetRecommendations = useCallback(() => {
    setRecommendations(null);
    setSelectedOptions({});
    setError(null);
  }, []);

  return {
    recommendations,
    selectedOptions,
    isGenerating,
    searchProgress,
    isConverting,
    error,
    buttonState,
    generateRecommendations,
    cancelGeneration,
    selectOption,
    getSelectedOption,
    getSelectedItinerary,
    convertToTrip,
    clearSelections,
    resetRecommendations,
    clearAllState
  };
};

export default usePremiumAI;
